import { useEffect, useRef } from 'react';
import { persistShaderState, readPersistedMouse, readPersistedTime } from './authShaderPersistence';

/**
 * Subtle, low-cost WebGL backdrop for the auth screens.
 *
 * A single full-screen fragment shader renders a slowly drifting,
 * domain-warped "aurora" in the Reflex brand greens. The cursor subtly
 * re-composes the whole frame — a gentle parallax on the ribbon field, a bias
 * on the domain warp, and a soft radial light halo whose center tracks the
 * pointer but stays damped within the central third of the screen. A strong
 * vignette fades the rim to black so the lit center reads as a glowing core in
 * darkness. Design constraints, in order of priority:
 *
 * - **Cheap.** One triangle, one pass, no textures. The backing store is
 *   rendered at a fraction of CSS resolution and DPR is capped, so the
 *   fragment count stays low even on hidpi displays. The RAF loop pauses
 *   when the tab is hidden.
 * - **Graceful fallback.** Everything is wrapped so a missing WebGL
 *   context, a failed compile, or a context loss simply leaves a
 *   transparent canvas — the CSS glow/grid behind it remain the backdrop.
 * - **Always dark aurora.** The canvas is transparent and composites over
 *   `bg-background`, but the `u_dark` uniform is pinned to 1 so the dark-mode
 *   palette/levels render in both light and dark mode.
 * - **Respectful.** `prefers-reduced-motion` renders a single static frame
 *   with no animation loop.
 */

const VERTEX_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SRC = `
precision mediump float;

uniform vec2 u_resolution;
uniform vec2 u_mouse;     // 0..1, y up
uniform float u_time;
uniform float u_dark;     // 1.0 dark, 0.0 light

// Reflex brand greens (see docs/internal/design/branding.md).
const vec3 GREEN_FRONT = vec3(0.408, 0.925, 0.769); // #68ecc4
const vec3 GREEN_MID   = vec3(0.165, 0.478, 0.388); // #2a7a63
const vec3 GREEN_DEEP  = vec3(0.078, 0.275, 0.227); // #14463a

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float aspect = u_resolution.x / u_resolution.y;

  // Cursor offset from screen center, in -1..1 on each axis. This drives the
  // whole composition rather than a local spotlight: a small value nudges the
  // warp, the palette, and a broad directional light across the full frame.
  vec2 mc = (u_mouse - 0.5) * 2.0;

  vec2 p = uv;
  p.x *= aspect;

  // Subtle whole-image parallax: the sampling origin drifts toward the cursor
  // so the entire ribbon field appears to shift as the mouse moves.
  p += mc * vec2(0.12, 0.09);

  float t = u_time * 0.035;

  // Domain warping for organic, flowing ribbons. The cursor biases the warp
  // offsets, so the shapes themselves reorganize slightly with the pointer.
  vec2 warp = mc * 0.5;
  vec2 q = vec2(fbm(p * 1.7 + vec2(0.0, t) + warp), fbm(p * 1.7 + vec2(5.2, 1.3) - t + warp));
  vec2 r = vec2(
    fbm(p * 1.7 + 2.4 * q + vec2(1.7, 9.2) + 0.6 * t),
    fbm(p * 1.7 + 2.4 * q + vec2(8.3, 2.8) - 0.5 * t)
  );
  float f = fbm(p * 1.7 + 3.2 * r);
  // Expand contrast so the ribbons read as defined shapes, but keep a wide
  // range (and a soft floor below) so low-density areas still carry a wash
  // instead of dropping out to bare background.
  f = clamp((f - 0.1) / 0.7, 0.0, 1.0);

  // Radial light halo. Its center tracks the cursor but is damped so it only
  // ever drifts within the central third of the screen: the highlight stays
  // anchored near the middle while still responding to the mouse. The 0.33
  // factor maps the full 0..1 cursor range onto a +/-1/6 offset from center.
  vec2 lightCenter = vec2(0.5) + (u_mouse - 0.5) * 0.33;
  // Aspect-correct the offset so the halo stays circular on wide viewports.
  vec2 lp = (uv - lightCenter) * vec2(aspect, 1.0);
  float halo = smoothstep(0.62, 0.0, length(lp));
  float glow = mix(0.16, 0.94, halo);

  // Dark swirl bands: where the warped field dips into its troughs, drive the
  // color toward the deepest green and add opacity, so defined dark tendrils
  // wind through the denser base wash. A second, offset trough band gives a
  // layered, more swirling structure than a single threshold.
  float swirl = smoothstep(0.62, 0.18, f);
  swirl = max(swirl, 0.7 * smoothstep(0.42, 0.26, f));

  // Two tuned palettes so the wash is clearly visible in both themes:
  // light mode leans on the deep/mid greens (which read on white), dark
  // mode leans on the brighter front green (which glows on black).
  vec3 lightCol = mix(mix(GREEN_MID, GREEN_DEEP, 0.25), GREEN_MID, glow * 0.6);
  lightCol = mix(lightCol, GREEN_DEEP, swirl * 0.9);
  vec3 darkCol = mix(GREEN_MID, GREEN_FRONT, f);
  darkCol = mix(darkCol, GREEN_FRONT, glow);
  darkCol = mix(darkCol, GREEN_DEEP, swirl * 0.85);
  vec3 col = mix(lightCol, darkCol, u_dark);

  // Softer field curve (mix of linear + squared) keeps the bright ribbons but
  // lifts the troughs, and a higher base floor means the wash reaches into the
  // corners instead of fading to white.
  float field = mix(f, f * f, 0.5);
  // Higher base keeps an even wash; the swirl term re-adds variation, but only
  // as darker, denser tendrils rather than bright ribbons.
  float lightA = 0.60 + 0.16 * field + 0.20 * swirl + 0.08 * glow;
  float darkA = 0.52 + 0.14 * field + 0.22 * swirl + 0.10 * glow;
  float alpha = mix(lightA, darkA, u_dark);

  // Concentrate the densest color around the vertical center, where the
  // sign-in card sits, and ease off symmetrically toward the top and bottom
  // edges. This frames the modal instead of pooling the green at the top,
  // which on tall (mobile) viewports left the bottom reading as empty.
  alpha *= mix(0.72, 1.0, 1.0 - smoothstep(0.08, 0.62, abs(uv.y - 0.5)));

  // Wide, gradual vignette: the lit core fills more of the frame and the
  // falloff extends further out toward the rim. The raw ramp is 1 in the
  // middle and eases toward the edges, but a low floor keeps the rim dim
  // rather than fully black: the edges retain a faint glow instead of
  // dropping to zero. We darken the color and lift the rim opacity by the same
  // amount so the falloff reads consistently.
  //
  // Each axis is normalized to -1..1 at its own edge (NOT aspect-corrected),
  // so the top/bottom edges vignette to the same level as the left/right ones
  // regardless of viewport width. Without this, a wide frame darkens the
  // sides far more than the top and bottom.
  vec2 vc = (uv - 0.5) * 2.0;
  float vignette = smoothstep(1.08, 0.30, length(vc));
  // Floor the ramp so it never reaches zero: edges stay low, not black.
  vignette = mix(0.07, 1.0, vignette);
  col *= vignette;
  float maxAlpha = mix(0.9, 0.86, u_dark);
  alpha = mix(maxAlpha, alpha, vignette);

  alpha = clamp(alpha, 0.0, maxAlpha);
  gl_FragColor = vec4(col, alpha);
}
`;

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext): WebGLProgram | null {
  const vert = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SRC);
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SRC);
  if (!vert || !frag) return null;
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

export function AuthShaderBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return;

    let gl: WebGLRenderingContext | null;
    try {
      gl = (canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: false,
        antialias: false,
      }) ||
        canvas.getContext('experimental-webgl', {
          alpha: true,
          premultipliedAlpha: false,
          antialias: false,
        })) as WebGLRenderingContext | null;
    } catch {
      gl = null;
    }
    if (!gl) return; // Fallback: CSS layers behind the canvas remain the backdrop.

    const program = createProgram(gl);
    if (!program) return;

    const glc = gl;
    const buffer = glc.createBuffer();
    glc.bindBuffer(glc.ARRAY_BUFFER, buffer);
    // Full-screen triangle.
    glc.bufferData(glc.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), glc.STATIC_DRAW);

    const aPosition = glc.getAttribLocation(program, 'a_position');
    const uResolution = glc.getUniformLocation(program, 'u_resolution');
    const uMouse = glc.getUniformLocation(program, 'u_mouse');
    const uTime = glc.getUniformLocation(program, 'u_time');
    const uDark = glc.getUniformLocation(program, 'u_dark');

    glc.useProgram(program);
    glc.enableVertexAttribArray(aPosition);
    glc.vertexAttribPointer(aPosition, 2, glc.FLOAT, false, 0, 0);
    glc.enable(glc.BLEND);
    glc.blendFunc(glc.SRC_ALPHA, glc.ONE_MINUS_SRC_ALPHA);

    // Render at a fraction of CSS resolution; the effect is soft so the
    // downscale is invisible but the fragment count drops dramatically.
    const RENDER_SCALE = 0.6;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The backdrop always renders with the dark-mode palette/levels, even in
    // light mode — the dark aurora is the intended look in both themes, so the
    // `u_dark` uniform is pinned to 1 rather than tracking the theme class.
    function isDark(): number {
      return 1;
    }

    let width = 0;
    let height = 0;
    function resize() {
      const w = Math.max(1, Math.floor(canvas!.clientWidth * dpr * RENDER_SCALE));
      const h = Math.max(1, Math.floor(canvas!.clientHeight * dpr * RENDER_SCALE));
      if (w === width && h === height) return;
      width = w;
      height = h;
      canvas!.width = w;
      canvas!.height = h;
      glc.viewport(0, 0, w, h);
    }

    // Smoothed cursor position (0..1, y up). Seed from the last persisted
    // value so a refresh reproduces the exact same shader frame; the target
    // starts at the same place so the field stays put until the pointer moves.
    const persistedMouse = readPersistedMouse();
    const mouse = { x: persistedMouse.x, y: persistedMouse.y };
    const target = { x: persistedMouse.x, y: persistedMouse.y };
    function onPointerMove(e: PointerEvent) {
      target.x = e.clientX / window.innerWidth;
      target.y = 1 - e.clientY / window.innerHeight;
    }
    if (!reduceMotion) window.addEventListener('pointermove', onPointerMove, { passive: true });

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    // Persist the animation clock so the smoke resumes from the same place
    // across reloads/navigations instead of snapping back to t=0. We seed the
    // local clock from the last persisted value and keep writing the current
    // time back (throttled) as the loop runs.
    let timeBase = readPersistedTime();
    let currentTime = timeBase;
    let lastPersist = 0;
    // Persist both the clock and the current cursor influence together so a
    // reload reproduces the exact same shader frame (same `u_time` and the
    // same `u_mouse`), not just the same animation phase.
    function persistTime(t: number) {
      persistShaderState(t, mouse);
    }

    let raf = 0;
    let running = true;
    let start = performance.now();

    function draw(now: number) {
      if (!running) return;
      resize();
      // Lerp the smoothed cursor toward its target; a small factor makes the
      // whole-image response drift in slowly rather than tracking 1:1.
      mouse.x += (target.x - mouse.x) * 0.025;
      mouse.y += (target.y - mouse.y) * 0.025;
      currentTime = reduceMotion ? 8.0 : timeBase + (now - start) / 1000;
      glc.uniform2f(uResolution, width, height);
      glc.uniform2f(uMouse, mouse.x, mouse.y);
      glc.uniform1f(uTime, currentTime);
      glc.uniform1f(uDark, isDark());
      glc.drawArrays(glc.TRIANGLES, 0, 3);
      // Throttle writes to ~once per second to avoid hammering localStorage.
      if (!reduceMotion && now - lastPersist > 1000) {
        lastPersist = now;
        persistTime(currentTime);
      }
      if (!reduceMotion) raf = requestAnimationFrame(draw);
    }
    // Paint the first frame synchronously at the persisted time so a refresh
    // shows the smoke already in place — no blank canvas / reset flash before
    // the RAF loop kicks in. `draw` also schedules the ongoing loop.
    draw(performance.now());

    function onVisibility() {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
        // Capture the clock before the tab is backgrounded; on resume we
        // re-base so the smoke doesn't jump forward by the hidden duration.
        if (!reduceMotion) persistTime(currentTime);
      } else if (!reduceMotion) {
        // Re-base the clock to "now" so the paused interval doesn't fast-forward
        // the animation; it picks up exactly where it left off.
        timeBase = currentTime;
        start = performance.now();
        lastPersist = 0;
        running = true;
        raf = requestAnimationFrame(draw);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    // localStorage writes can be lost on hard refresh if we only persist in the
    // RAF loop; pagehide is the reliable last-chance hook before unload.
    function onPageHide() {
      if (!reduceMotion) persistTime(currentTime);
    }
    window.addEventListener('pagehide', onPageHide);

    function onContextLost(e: Event) {
      e.preventDefault();
      running = false;
      cancelAnimationFrame(raf);
    }
    canvas.addEventListener('webglcontextlost', onContextLost);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      if (!reduceMotion) persistTime(currentTime);
      ro.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      glc.deleteProgram(program);
      glc.deleteBuffer(buffer);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="auth-shader-canvas"
      aria-hidden
      className="absolute inset-0 h-full w-full"
    />
  );
}
